"use strict";
// SPDX-FileCopyrightText: 2024 Red Bee Media Ltd <https://www.redbeemedia.com/>
//
// SPDX-License-Identifier: MIT
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.DashJs = exports.getCapabilitiesFilter = void 0;
const dashjs_1 = __importStar(require("dashjs"));
const dash_mss_min_1 = require("dashjs/dist/dash.mss.min");
const lodash_merge_1 = __importDefault(require("lodash.merge"));
const js_player_shared_1 = require("@ericssonbroadcastservices/js-player-shared");
const common_1 = require("../device/common");
const helpers_1 = require("../utils/helpers");
const preferences_1 = require("../utils/preferences");
const AbstractBaseEngine_1 = require("./AbstractBaseEngine");
const defaultConfigurations_1 = require("./defaultConfigurations");
const interfaces_1 = require("./interfaces");
const dashJsPatches_1 = require("./utils/dashJsPatches");
// Set MssHandler to the global dashjs object to allow for SmoothStreaming playback
dashjs_1.default.MssHandler = dash_mss_min_1.MssHandler;
const HEVC_REGEX = new RegExp(/hev1|hvc1/);
function getErrorTypeFromCode(code) {
    var _a;
    const category = ((_a = Object.entries(dashjs_1.MediaPlayer.errors).find(([, value]) => value === code)) === null || _a === void 0 ? void 0 : _a[0]) || "";
    if (category.includes("REQUEST"))
        return js_player_shared_1.ErrorTypes.NETWORK;
    if (category.includes("CAPABILITY"))
        return js_player_shared_1.ErrorTypes.CODEC;
    if (category.includes("KEY"))
        return js_player_shared_1.ErrorTypes.DRM;
    if (category.includes("MANIFEST"))
        return js_player_shared_1.ErrorTypes.MANIFEST;
    if (category.includes("FRAGMENT") || category.includes("SEGMENT")) {
        return js_player_shared_1.ErrorTypes.SEGMENT;
    }
    if (category.includes("MEDIA"))
        return js_player_shared_1.ErrorTypes.MEDIA;
    return js_player_shared_1.ErrorTypes.OTHER;
}
function createTrack(track) {
    var _a, _b, _c;
    let kind;
    if (track.type === "audio") {
        kind = (0, AbstractBaseEngine_1.getAudioKind)((_a = track.roles) !== null && _a !== void 0 ? _a : []);
    }
    else if (track.type === "text") {
        kind = (0, AbstractBaseEngine_1.getTextKind)((_b = track.roles) !== null && _b !== void 0 ? _b : []);
    }
    return {
        id: track.id, // number although dash.js typings says string...
        language: track.lang || "",
        // @ts-ignore, invalid typings from dashjs
        label: ((_c = track.labels[0]) === null || _c === void 0 ? void 0 : _c.text) || (0, js_player_shared_1.getLabel)(track.lang),
        kind,
        raw: track,
    };
}
// Custom capabilities filter to filter out `hvc1.*.*.*.B0` codecs on LG as well as high framerates.
function getCapabilitiesFilter(isHEVCDrmSupported) {
    return (representation) => {
        if ((0, common_1.isLGTV)() && (0, common_1.getBrowserVersion)() === 38) {
            if ((representation.frameRate && representation.frameRate > 30) ||
                (representation.codecs &&
                    HEVC_REGEX.test(representation.codecs) &&
                    representation.codecs.includes(".B0"))) {
                return false;
            }
        }
        if (isHEVCDrmSupported === false &&
            representation.codecs &&
            HEVC_REGEX.test(representation.codecs)) {
            return false;
        }
        return true;
    };
}
exports.getCapabilitiesFilter = getCapabilitiesFilter;
// Sort priority of video tracks so that HEVC is first ( if supported )
function initialTrackSelection(tracks) {
    const sorted = tracks.sort((a, b) => {
        if (a.codec &&
            b.codec &&
            HEVC_REGEX.test(a.codec) &&
            !HEVC_REGEX.test(b.codec)) {
            return -1;
        }
        return 0;
    });
    return sorted;
}
class DashJs extends AbstractBaseEngine_1.AbstractBaseEngine {
    constructor(videoElement, instanceSettings) {
        super(videoElement, instanceSettings);
        this.name = "Dashjs";
        this.playertechVersion = "";
        this.audioSettings = {};
        (0, dashJsPatches_1.patchMediaSourceChrome53)();
        this.mediaPlayer = (0, dashjs_1.MediaPlayer)().create();
        this.playertechVersion = this.mediaPlayer.getVersion();
        this.mediaPlayer.initialize();
        this.mediaPlayer.attachView(videoElement);
        // autoplay is handled on our side to guarantee a proper eventflow
        this.mediaPlayer.setAutoPlay(false);
        this.mediaPlayer.setCustomInitialTrackSelectionFunction(initialTrackSelection);
        this.setupEventListeners();
    }
    setupEventListeners() {
        var _a, _b;
        super.setupEventListeners();
        this.mediaPlayer.on(dashjs_1.MediaPlayer.events.KEY_STATUSES_CHANGED, () => {
            switch (this.keySystem) {
                case js_player_shared_1.KeySystem.PLAYREADY:
                    this.emit(interfaces_1.EngineEvents.DRM_UPDATE, "PLAYREADY_LICENSE_REQUEST");
                    this.emit(interfaces_1.EngineEvents.DRM_UPDATE, "PLAYREADY_LICENSE_RESPONSE");
                    break;
                case js_player_shared_1.KeySystem.WIDEVINE:
                    this.emit(interfaces_1.EngineEvents.DRM_UPDATE, "WIDEVINE_LICENSE_REQUEST");
                    this.emit(interfaces_1.EngineEvents.DRM_UPDATE, "WIDEVINE_LICENSE_RESPONSE");
                    break;
            }
        });
        this.mediaPlayer.on(dashjs_1.MediaPlayer.events.KEY_ERROR, () => {
            switch (this.keySystem) {
                case js_player_shared_1.KeySystem.PLAYREADY:
                    this.emit(interfaces_1.EngineEvents.DRM_UPDATE, "PLAYREADY_LICENSE_ERROR");
                    break;
                case js_player_shared_1.KeySystem.WIDEVINE:
                    this.emit(interfaces_1.EngineEvents.DRM_UPDATE, "WIDEVINE_LICENSE_ERROR");
                    break;
            }
        });
        this.mediaPlayer.on(dashjs_1.MediaPlayer.events.QUALITY_CHANGE_RENDERED, (data) => {
            if (data.mediaType !== "video")
                return;
            const level = data.newQuality;
            const bitrates = this.mediaPlayer.getBitrateInfoListFor("video");
            const newLevel = bitrates[level];
            if (newLevel) {
                this.emit(interfaces_1.EngineEvents.BITRATE_CHANGED, {
                    bitrate: newLevel.bitrate,
                    width: newLevel.width,
                    height: newLevel.height,
                });
            }
        });
        this.mediaPlayer.on(dashjs_1.MediaPlayer.events.TRACK_CHANGE_RENDERED, (_event) => {
            const event = _event; // missing dash.js type
            switch (event.mediaType) {
                case "audio":
                    this.onAudioTrackChange();
                    break;
                case "text":
                    this.setSubtitleTrack(this.getInitialSubtitleTrack(), false);
                    break;
            }
        });
        const schemeIdURIs = (_b = (_a = this.instanceSettings.initOptions) === null || _a === void 0 ? void 0 : _a.metadataURIs) !== null && _b !== void 0 ? _b : [];
        schemeIdURIs.forEach((uri) => {
            this.mediaPlayer.on(uri, (event) => {
                this.emit(interfaces_1.EngineEvents.METADATA_EVENT, {
                    event,
                    engineName: this.name,
                    engineVersion: this.playertechVersion,
                });
            });
        });
        this.mediaPlayer.on(dashjs_1.MediaPlayer.events.ERROR, this.onDashJsError.bind(this));
        this.mediaPlayer.on(dashjs_1.MediaPlayer.events.PLAYBACK_ERROR, this.onDashJsError.bind(this));
        this.mediaPlayer.on(dashjs_1.MediaPlayer.events.KEY_ERROR, this.onDashJsError.bind(this));
        this.mediaPlayer.on(dashjs_1.MediaPlayer.events.PERIOD_SWITCH_COMPLETED, () => {
            // Workaround for https://github.com/Dash-Industry-Forum/dash.js/issues/3804
            const track = this.mediaPlayer
                .getTracksFor("audio")
                .find((t) => t.lang === this.audioSettings.language &&
                (0, AbstractBaseEngine_1.getAudioKind)(t.roles || []) === this.audioSettings.kind);
            if (track && track !== this.getCurrentTrackFor("audio")) {
                this.mediaPlayer.setCurrentTrack(track);
            }
            // force a textTrack change when period changes. Dash.js will force set
            // the used textTrack to "showing" we need it "hidden"
            this.setSubtitleTrack(this.getInitialSubtitleTrack(), false);
            // trigger onTracksChange to make sure the state is up-to-date with all the relevant tracks.
            this.onTracksChange();
        });
        // 😐🔫 Nowtilus provide a `<pssh>` element instead of `<cenc:pssh>` which is not valid...
        // TODO: EMP-18318 remove this once the ticket has been resolved.
        this.mediaPlayer.on(dashjs_1.MediaPlayer.events.MANIFEST_LOADED, ({ data }) => {
            var _a;
            (_a = data.Period_asArray) === null || _a === void 0 ? void 0 : _a.forEach((period) => {
                var _a;
                (_a = period.AdaptationSet_asArray) === null || _a === void 0 ? void 0 : _a.forEach((adaptationSet) => {
                    var _a;
                    (_a = adaptationSet.ContentProtection_asArray) === null || _a === void 0 ? void 0 : _a.forEach((contentProtection) => {
                        // dash.js expects the xml2json parser to generate an object with { __text: string }
                        // but we get a string directly, this simply changes that so it matches the interface expected
                        // by dash.js
                        // NOTE! if dash.js is updated to NOT provide the manifest by reference anymore this won't work...
                        const pssh = contentProtection.pssh;
                        if (typeof pssh === "string") {
                            contentProtection.pssh = {
                                __text: pssh,
                            };
                        }
                    });
                });
            });
        });
    }
    getCurrentTrackFor(type) {
        try {
            // TODO: there is no null check here:https://github.com/Dash-Industry-Forum/dash.js/blame/development/src/streaming/MediaPlayer.js#L1588
            // therefore we need to wrap this in a try catch. When that is fixed this workaround can be removed
            return this.mediaPlayer.getCurrentTrackFor(type);
        }
        catch (e) {
            return null;
        }
    }
    setupStartGapDetection() {
        // Workaround for dash.js not detecting gaps in the beginning of a stream
        const onBufferLevelUpdated = (e) => {
            var _a, _b;
            if (!("mediaType" in e) ||
                !("bufferLevel" in e) ||
                e.mediaType === "text" ||
                this.getPlaybackState() !== js_player_shared_1.PlaybackState.LOADING) {
                return;
            }
            const processor = (_b = (_a = this.mediaPlayer) === null || _a === void 0 ? void 0 : _a.getActiveStream()) === null || _b === void 0 ? void 0 : _b.getProcessors().find((processor) => processor.getType() === e.mediaType);
            const bufferRanges = processor === null || processor === void 0 ? void 0 : processor.getBuffer().getAllBufferRanges();
            if (bufferRanges.length) {
                const start = bufferRanges.start(0);
                // If the video/audio doesn't have anything buffered (bufferLevel) but there is a startTime dash.js has infact loaded _something_
                // for that mediaType, so we can safely assume that there is indeed a gap and we should seek to whatever is actually buffered.
                if (e.bufferLevel === 0 && start > 0) {
                    this.mediaPlayer.off(dashjs_1.MediaPlayer.events.BUFFER_LEVEL_UPDATED, onBufferLevelUpdated);
                    this.videoElement.currentTime = start;
                }
            }
        };
        this.mediaPlayer.on(dashjs_1.MediaPlayer.events.BUFFER_LEVEL_UPDATED, onBufferLevelUpdated);
    }
    _load({ src, license, startTime, subtitle, audio, }) {
        const _super = Object.create(null, {
            load: { get: () => super.load }
        });
        return __awaiter(this, void 0, void 0, function* () {
            this.src = src;
            const customConfiguration = this.instanceSettings.initOptions.customDashJSConfiguration || {};
            const configuration = (0, lodash_merge_1.default)({}, defaultConfigurations_1.DashJsDefaults, customConfiguration, {
                streaming: {
                    text: {
                        defaultEnabled: !!(subtitle === null || subtitle === void 0 ? void 0 : subtitle.language),
                    },
                    trackSwitchMode: {
                        // Always discard previous audio track (language) buffer when switching
                        // Also need this for the https://github.com/Dash-Industry-Forum/dash.js/issues/3804 workaround
                        audio: "alwaysReplace",
                    },
                },
            });
            this.mediaPlayer.updateSettings(configuration);
            const isHEVCDrmSupported = yield (0, helpers_1.getSupportedDrm)(this.instanceSettings.supportedKeySystem, "hvc1.1.6.L93.90");
            this.mediaPlayer.registerCustomCapabilitiesFilter(getCapabilitiesFilter(!!isHEVCDrmSupported));
            if (audio === null || audio === void 0 ? void 0 : audio.language) {
                this.audioSettings = audio;
                this.mediaPlayer.setInitialMediaSettingsFor("audio", {
                    lang: audio.language,
                    role: (0, AbstractBaseEngine_1.getDashAudioKind)(audio.kind),
                });
            }
            if (subtitle === null || subtitle === void 0 ? void 0 : subtitle.language) {
                this.mediaPlayer.setInitialMediaSettingsFor("text", {
                    lang: subtitle.language,
                    role: (0, AbstractBaseEngine_1.getDashTextKind)(subtitle.kind),
                });
            }
            this.setContractRestrictedConfiguration();
            this.setContentSpecificConfiguration();
            this.setLicense(this.instanceSettings.supportedKeySystem, license);
            if (startTime === undefined || startTime === 0) {
                this.setupStartGapDetection();
            }
            this.mediaPlayer.attachSource(startTime === undefined ? src : `${src}#t=${startTime}`);
            this.once(interfaces_1.EngineEvents.LOADED, () => {
                // when switching sourceBuffers this needs to be set to true otherwise dash.js will pause
                // this happens if reuseExistingSourceBuffers is `false` or when switching from period without DRM to one with.
                this.videoElement.autoplay = true;
            });
            _super.load.call(this, {
                src,
                license,
                startTime,
                audio,
                subtitle,
            });
        });
    }
    load(parameters) {
        // load doesn't return a promise but since we want to use async/await we call a private method
        this._load(parameters);
    }
    onLoaded() {
        var _a, _b, _c, _d;
        const presentationTimeOffset = (_d = (_c = (_b = (_a = this.mediaPlayer
            .getDashMetrics()) === null || _a === void 0 ? void 0 : _a.getCurrentManifestUpdate()) === null || _b === void 0 ? void 0 : _b.representationInfo) === null || _c === void 0 ? void 0 : _c[0]) === null || _d === void 0 ? void 0 : _d.presentationTimeOffset;
        if (!this.isLive() && presentationTimeOffset) {
            this.setUTCStartTime(presentationTimeOffset * 1000);
        }
        super.onLoaded();
    }
    setPreferredLanguages() {
        // no-op
    }
    addSubtitleEvents() {
        // no-op
    }
    addAudioTrackEvents() {
        // no-op
    }
    isLive() {
        var _a, _b, _c;
        const isDynamic = this.mediaPlayer.isReady()
            ? this.mediaPlayer.isDynamic()
            : null;
        if (isDynamic !== null) {
            return isDynamic;
        }
        return (_c = (_b = (_a = this.instanceSettings.playResponse) === null || _a === void 0 ? void 0 : _a.streamInfo) === null || _b === void 0 ? void 0 : _b.live) !== null && _c !== void 0 ? _c : false;
    }
    setContractRestrictedConfiguration() {
        var _a;
        if (!this.instanceSettings.playResponse) {
            return;
        }
        const { minBitrate, maxBitrate, maxResHeight } = ((_a = this.instanceSettings.playResponse) === null || _a === void 0 ? void 0 : _a.contractRestrictions) || {};
        if (!minBitrate && !maxBitrate && !maxResHeight) {
            return;
        }
        const settings = {
            streaming: {
                abr: Object.assign(Object.assign({}, (minBitrate && { minBitrate: { audio: -1, video: minBitrate } })), (maxBitrate && { maxBitrate: { audio: -1, video: maxBitrate } })),
            },
        };
        this.mediaPlayer.updateSettings(settings);
    }
    setContentSpecificConfiguration() {
        var _a, _b, _c;
        // http://cdn.dashjs.org/latest/jsdoc/module-Settings.html
        const settings = {
            streaming: {
                delay: {},
            },
        };
        const currentPlaybackObject = (_b = (_a = this.instanceSettings) === null || _a === void 0 ? void 0 : _a.formats) === null || _b === void 0 ? void 0 : _b.find((f) => f.mediaLocator === this.src);
        if (currentPlaybackObject === null || currentPlaybackObject === void 0 ? void 0 : currentPlaybackObject.liveDelay) {
            settings.streaming.delay.liveDelay =
                currentPlaybackObject.liveDelay / 1000; // DashJS expects the live delay to be in seconds
            settings.streaming.delay.useSuggestedPresentationDelay = false;
        }
        if ((_c = this.instanceSettings.mediaInfo) === null || _c === void 0 ? void 0 : _c.lowLatency) {
            this.mediaPlayer.updateSettings(defaultConfigurations_1.DashJsLowLatency);
        }
        this.mediaPlayer.updateSettings(settings);
    }
    setLicense(supportedKeySystem, license) {
        var _a;
        if (!supportedKeySystem || !license) {
            return;
        }
        const licenseObject = license[supportedKeySystem];
        if (licenseObject && supportedKeySystem) {
            this.keySystem = supportedKeySystem;
            let keySystemOptions = {};
            keySystemOptions[supportedKeySystem] = {
                serverURL: licenseObject.licenseServerUrl,
                priority: 0, // 0 = highest prio
            };
            if ((_a = this.instanceSettings.playResponse) === null || _a === void 0 ? void 0 : _a.playToken) {
                keySystemOptions[supportedKeySystem].httpRequestHeaders = {
                    authorization: `Bearer ${this.instanceSettings.playResponse.playToken}`,
                };
                keySystemOptions[supportedKeySystem].withCredentials = false;
            }
            if (supportedKeySystem === js_player_shared_1.KeySystem.WIDEVINE) {
                keySystemOptions = this.setWidevineSpecifics(keySystemOptions);
            }
            this.mediaPlayer.setProtectionData(keySystemOptions);
        }
    }
    setWidevineSpecifics(keySystemOptions) {
        keySystemOptions[js_player_shared_1.KeySystem.WIDEVINE].videoRobustness = "SW_SECURE_CRYPTO";
        keySystemOptions[js_player_shared_1.KeySystem.WIDEVINE].audioRobustness = "SW_SECURE_CRYPTO";
        return keySystemOptions;
    }
    getSeekable() {
        var _a, _b;
        if (!this.mediaPlayer.isReady()) {
            return super.getSeekable();
        }
        const dvrInfo = (_b = (_a = this.mediaPlayer) === null || _a === void 0 ? void 0 : _a.getDashMetrics()) === null || _b === void 0 ? void 0 : _b.getCurrentDVRInfo("video");
        if (dvrInfo) {
            return dvrInfo.range;
        }
        return super.getSeekable();
    }
    onError() {
        // no-op, handled by onDashJsError
    }
    onDashJsError({ error }) {
        const playerError = new js_player_shared_1.DashJsPlayerError(error.message, {
            type: getErrorTypeFromCode(error.code),
            code: error.code,
            rawError: error,
        });
        if (playerError) {
            this.emit(interfaces_1.EngineEvents.ERROR, playerError);
        }
    }
    /**
     *  hide the active textTrack and dispatch cues as events to be rendered
     *  as HTML instead of WebVTT.
     */
    handleActiveTextTrack(textTrack) {
        Array.from(this.videoElement.textTracks).forEach((track) => {
            if (track !== textTrack) {
                track.mode = "disabled";
            }
        });
        if (!textTrack) {
            this.activeTrack = undefined;
            return;
        }
        this.activeTrack = textTrack;
        textTrack.mode = "hidden";
        const activeCues = (textTrack.activeCues ? Array.from(textTrack.activeCues) : []);
        this.emit(interfaces_1.EngineEvents.SUBTITLE_CUE_CHANGED, activeCues);
        textTrack.oncuechange = () => {
            const activeCues = (textTrack.activeCues ? Array.from(textTrack.activeCues) : []);
            this.emit(interfaces_1.EngineEvents.SUBTITLE_CUE_CHANGED, activeCues);
        };
    }
    onTextTracksChange(shouldUpdatePreferences = true) {
        if (
        // if the mediaPlayer no longer have tracks but the videoElement does
        // it means that there was a period switch that temporarily removes tracks
        // ignore this since when the textTracks are back we don't want them disabled.
        this.mediaPlayer.getTracksFor("text").length === 0 &&
            this.videoElement.textTracks.length > 0) {
            return;
        }
        const track = this.getSubtitleTrack();
        this.emit(interfaces_1.EngineEvents.SUBTITLE_CHANGED, {
            track,
            shouldUpdatePreferences,
        });
        this.emit(interfaces_1.EngineEvents.SUBTITLE_CUE_CHANGED, []);
        if (!track && this.activeTrack) {
            this.handleActiveTextTrack(undefined);
            return;
        }
        for (let i = 0, len = this.videoElement.textTracks.length; i < len; i++) {
            const textTrack = this.videoElement.textTracks[i];
            if (track && textTrack.mode === "showing") {
                this.handleActiveTextTrack(textTrack);
            }
        }
    }
    setLowLatencyLiveCatchup(enabled) {
        // @ts-ignore, getLowLatencyModeEnabled() missing from type definition
        if (!this.mediaPlayer.getLowLatencyModeEnabled()) {
            return;
        }
        this.mediaPlayer.updateSettings({
            streaming: {
                liveCatchup: {
                    enabled,
                },
            },
        });
    }
    seekTo(pos) {
        this.setLowLatencyLiveCatchup(false);
        super.seekTo(pos);
    }
    seekToLive() {
        this.setLowLatencyLiveCatchup(true);
        super.seekToLive();
    }
    getUTCCurrentTime() {
        var _a, _b;
        if (!this.mediaPlayer.isReady()) {
            return 0;
        }
        if (this.isLive()) {
            return (_b = ((_a = this.mediaPlayer) === null || _a === void 0 ? void 0 : _a.timeAsUTC()) * 1000) !== null && _b !== void 0 ? _b : 0;
        }
        return super.getUTCCurrentTime();
    }
    getUTCDuration() {
        var _a, _b;
        if (!this.mediaPlayer.isReady()) {
            return 0;
        }
        if (this.isLive()) {
            return (_b = ((_a = this.mediaPlayer) === null || _a === void 0 ? void 0 : _a.durationAsUTC()) * 1000) !== null && _b !== void 0 ? _b : 0;
        }
        return super.getUTCDuration();
    }
    getUTCSeekable() {
        if (!this.mediaPlayer.isReady()) {
            return { start: -1, end: -1 };
        }
        if (this.isLive()) {
            const seekable = this.getSeekable();
            return {
                start: seekable.start * 1000,
                end: seekable.end * 1000,
            };
        }
        return super.getUTCSeekable();
    }
    setSubtitleTrack(track, shouldUpdatePreferences) {
        var _a, _b;
        if (!this.mediaPlayer.isReady()) {
            return;
        }
        let trackIndex = -1;
        if (track && track.id) {
            const textTracks = ((_a = this.mediaPlayer) === null || _a === void 0 ? void 0 : _a.getTracksFor("text")) || [];
            trackIndex = textTracks.findIndex((t) => t.id === track.id);
        }
        else {
            const textTracks = ((_b = this.mediaPlayer) === null || _b === void 0 ? void 0 : _b.getTracksFor("text")) || [];
            trackIndex = textTracks.findIndex((t) => { var _a; return (_a = t.roles) === null || _a === void 0 ? void 0 : _a.includes("forced-subtitle"); });
        }
        const enableTrack = trackIndex !== -1;
        this.mediaPlayer.setTextTrack(trackIndex);
        this.mediaPlayer.enableText(enableTrack);
        this.mediaPlayer.enableForcedTextStreaming(enableTrack);
        this.onTextTracksChange(shouldUpdatePreferences);
    }
    getSubtitleTrack() {
        var _a, _b;
        if (!((_a = this.mediaPlayer) === null || _a === void 0 ? void 0 : _a.isReady())) {
            return;
        }
        const track = this.getCurrentTrackFor("text");
        if ((_b = track === null || track === void 0 ? void 0 : track.roles) === null || _b === void 0 ? void 0 : _b.includes("forced-subtitle")) {
            this.mediaPlayer.enableText(true);
            return createTrack(track);
        }
        if (this.mediaPlayer.isTextEnabled() && track) {
            return createTrack(track);
        }
    }
    getInitialSubtitleTrack() {
        var _a;
        const textTracks = (_a = this.mediaPlayer) === null || _a === void 0 ? void 0 : _a.getTracksFor("text");
        const preferedSubtitles = (0, preferences_1.getPreferences)().subtitle;
        const initialTrack = textTracks.find((track) => {
            const kind = track.roles && (0, AbstractBaseEngine_1.getTextKind)(track.roles);
            return (track.lang === (preferedSubtitles === null || preferedSubtitles === void 0 ? void 0 : preferedSubtitles.language) &&
                kind === preferedSubtitles.kind);
        });
        return initialTrack && createTrack(initialTrack);
    }
    getSubtitleTracks() {
        var _a, _b, _c;
        if (!this.mediaPlayer.isReady()) {
            return [];
        }
        return this.filterVisibleSubtitleTracks(((_c = (_b = (_a = this.mediaPlayer) === null || _a === void 0 ? void 0 : _a.getTracksFor("text")) === null || _b === void 0 ? void 0 : _b.map((track) => createTrack(track))) !== null && _c !== void 0 ? _c : []).filter((track, index, array) => array.findIndex((compTrack) => track.language === compTrack.language &&
            track.label === compTrack.label &&
            track.kind === compTrack.kind) === index));
    }
    setAudioTrack(track) {
        var _a, _b;
        if (!this.mediaPlayer.isReady()) {
            return [];
        }
        const tracks = (_b = (_a = this.mediaPlayer) === null || _a === void 0 ? void 0 : _a.getTracksFor("audio")) !== null && _b !== void 0 ? _b : [];
        const internalTrack = tracks.find((t) => t.id === track.id);
        if (internalTrack) {
            this.audioSettings = {
                language: internalTrack.lang || undefined,
                kind: (0, AbstractBaseEngine_1.getAudioKind)(internalTrack.roles || []),
            };
            this.mediaPlayer.setCurrentTrack(internalTrack);
        }
    }
    getAudioTrack() {
        if (!this.mediaPlayer.isReady()) {
            return;
        }
        const track = this.getCurrentTrackFor("audio");
        if (track) {
            return createTrack(track);
        }
    }
    getAudioTracks() {
        var _a, _b, _c;
        if (!this.mediaPlayer.isReady()) {
            return [];
        }
        return ((_c = (_b = (_a = this.mediaPlayer) === null || _a === void 0 ? void 0 : _a.getTracksFor("audio")) === null || _b === void 0 ? void 0 : _b.map((track) => createTrack(track))) !== null && _c !== void 0 ? _c : []);
    }
    getQualityLevel() {
        if (!this.mediaPlayer.isReady()) {
            return super.getQualityLevel();
        }
        const index = this.mediaPlayer.getQualityFor("video");
        if (index > -1) {
            const qualityLevel = this.getQualityLevels().find((level) => level.id === index);
            if (qualityLevel) {
                return qualityLevel;
            }
        }
        return super.getQualityLevel();
    }
    getQualityLevels() {
        var _a, _b;
        if (!this.mediaPlayer.isReady()) {
            return [];
        }
        const qualityLevels = (_b = (_a = this.mediaPlayer) === null || _a === void 0 ? void 0 : _a.getBitrateInfoListFor("video")) === null || _b === void 0 ? void 0 : _b.map((bitrateInfo, index) => {
            const qualityLevel = {
                id: index,
                bandwidth: bitrateInfo.bitrate,
                width: bitrateInfo.width,
                height: bitrateInfo.height,
            };
            qualityLevel.name = (0, AbstractBaseEngine_1.getQualityLevelName)(qualityLevel);
            return qualityLevel;
        });
        return [...super.getQualityLevels(), ...qualityLevels];
    }
    setQualityLevel(level) {
        if (!this.mediaPlayer.isReady()) {
            return;
        }
        if (level.id === js_player_shared_1.AUTO_QUALITY_LEVEL_DEFINITION.id) {
            this.mediaPlayer.updateSettings({
                streaming: {
                    abr: {
                        autoSwitchBitrate: {
                            video: true,
                        },
                    },
                },
            });
        }
        else {
            this.mediaPlayer.updateSettings({
                streaming: {
                    abr: {
                        autoSwitchBitrate: {
                            video: false,
                        },
                    },
                },
            });
            this.mediaPlayer.setQualityFor("video", level.id, true);
        }
    }
    destroy() {
        this.mediaPlayer.destroy();
        super.destroy();
    }
}
exports.DashJs = DashJs;
//# sourceMappingURL=DashJs.js.map